package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.SettingsStrings
import com.milta.feature.aicredit.domain.usecase.GetMyAiCreditHistoryUseCase
import com.milta.feature.aicredit.presentation.AiCreditStore
import com.milta.feature.aicredit.presentation.MyAiCreditIntent
import com.milta.feature.aicredit.presentation.MyAiCreditViewModel
import com.milta.feature.aicredit.presentation.actionLabel
import com.milta.feature.aicredit.presentation.percent
import com.milta.feature.aicredit.presentation.share
import com.milta.feature.optimizationquota.domain.usecase.GetMyOptimizationHistoryUseCase
import com.milta.feature.optimizationquota.presentation.MyOptimizationsIntent
import com.milta.feature.optimizationquota.presentation.MyOptimizationsViewModel
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaStore
import com.milta.feature.optimizationquota.presentation.ratioLabel
import com.milta.feature.profile.domain.usecase.GetProfileUseCase
import com.milta.feature.profile.domain.usecase.UpdateLanguageUseCase
import com.milta.feature.settings.presentation.WebSettingsIntent
import com.milta.feature.settings.presentation.WebSettingsNavEvent
import com.milta.feature.settings.presentation.WebSettingsState
import com.milta.feature.settings.presentation.WebSettingsViewModel
import com.milta.feature.subscription.domain.entity.SubscriptionPlan
import com.milta.feature.subscription.domain.usecase.GetMySubscriptionUseCase
import com.milta.feature.subscription.domain.usecase.GetSubscriptionPlansUseCase
import com.milta.feature.team.domain.usecase.DeleteEnterpriseUseCase
import com.milta.feature.team.domain.usecase.GetMyEnterpriseUseCase
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

private data class LanguageOption(val code: String, val nativeName: String, val flagFile: String)

/** MIL-53 — the 10 languages the Dashboard Web language picker must offer, native (endonym) names,
 * never translated by the current interface language (matches [com.milta.feature.profile.presentation.ProfileScreen]'s
 * `languageDisplayName` convention on mobile). Same flag asset files as [WebWelcomeScreen]'s
 * language picker — `flags_flag_de.png` is the one exception generated for this ticket (German is
 * Dashboard-Web-only, see [[decision:mil53-german-tenth-language-fallback]], so no German flag
 * existed among the other 9 languages' assets yet). */
private val SETTINGS_LANGUAGES = listOf(
    LanguageOption("fr", "Français", "flags_flag_fr.png"),
    LanguageOption("nl", "Nederlands", "flags_flag_nl.png"),
    LanguageOption("de", "Deutsch", "flags_flag_de.png"),
    LanguageOption("en", "English", "flags_flag_en.png"),
    LanguageOption("ro", "Română", "flags_flag_ro.png"),
    LanguageOption("bg", "Български", "flags_flag_bg.png"),
    LanguageOption("sq", "Shqip", "flags_flag_sq.png"),
    LanguageOption("pl", "Polski", "flags_flag_pl.png"),
    LanguageOption("it", "Italiano", "flags_flag_it.png"),
    LanguageOption("es", "Español", "flags_flag_es.png"),
)

/**
 * Entry point wired from `Main.kt`'s "Paramètres" nav destination (MIL-53) — Organisation profile
 * (read-only), interface language, subscription (read-only) and the Organisation-deletion critical
 * zone. Two-column layout per the validated mockup: narrower left column (profile + language),
 * wider right column (subscription + critical zone).
 */
@Composable
fun WebSettingsContent(
    onLanguageChanged: (String) -> Unit,
    onOrganizationDeleted: () -> Unit,
    onNavigateToTerms: () -> Unit = {},
    onNavigateToPrivacy: () -> Unit = {},
) {
    val getProfile: GetProfileUseCase = koinInject()
    val updateLanguage: UpdateLanguageUseCase = koinInject()
    val getMyEnterprise: GetMyEnterpriseUseCase = koinInject()
    val getMySubscription: GetMySubscriptionUseCase = koinInject()
    val getSubscriptionPlans: GetSubscriptionPlansUseCase = koinInject()
    val deleteEnterprise: DeleteEnterpriseUseCase = koinInject()
    val viewModel = remember {
        WebSettingsViewModel(getProfile, updateLanguage, getMyEnterprise, getMySubscription, getSubscriptionPlans, deleteEnterprise)
    }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(viewModel) {
        viewModel.navEvent.collect { event ->
            when (event) {
                WebSettingsNavEvent.OrganizationDeleted -> onOrganizationDeleted()
            }
        }
    }

    // Mirrors ProfileScreen's `LaunchedEffect(state.language) { onLanguageChange(lang) }` — applies
    // both the initial load (Account.Language) and any explicit change to the whole web interface
    // immediately, never the Chauffeur's own mobile-side language (separate account field).
    LaunchedEffect(state.language) {
        val lang = state.language
        if (!lang.isNullOrBlank()) onLanguageChanged(lang)
    }

    val strings = LocalStrings.current.settings
    val commonStrings = LocalStrings.current.common
    val subscriptionStrings = LocalStrings.current.subscription

    Div(
        attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "1fr 1.35fr")
                property("gap", "18px")
                property("align-items", "start")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
            ProfileCard(strings, state)
            LanguageCard(strings, state.language ?: "en", state.languageError) { viewModel.onIntent(WebSettingsIntent.SelectLanguage(it)) }
            LegalCard(strings, onNavigateToTerms = onNavigateToTerms, onNavigateToPrivacy = onNavigateToPrivacy)
        }
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
            SubscriptionCard(strings, subscriptionStrings, state)
            // MIL-97 — E3: the Enterprise account's own counter and its paged history (R1, R12, R13).
            MyUsageCard()
            DeleteZoneCard(strings, commonStrings, state, onIntent = viewModel::onIntent)
        }
    }

    if (state.showDeleteConfirmDialog) {
        DeleteConfirmDialog(strings, commonStrings, state, onIntent = viewModel::onIntent)
    }
}

@Composable
private fun SettingsCard(title: String, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-radius", "12px")
                padding(18.px)
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700")
                    property("letter-spacing", "0.06em"); property("text-transform", "uppercase")
                    property("color", WebTextSecondary); property("margin-bottom", "14px")
                }
            },
        ) { Text(title) }
        content()
    }
}

@Composable
private fun ProfileCard(strings: SettingsStrings, state: WebSettingsState) {
    SettingsCard(title = strings.profileSectionTitle) {
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-bottom", "6px") } }) {
            Text(strings.profileNameLabel)
        }
        Div(
            attrs = {
                style {
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    padding(11.px, 14.px); property("font-size", "13.5px"); property("font-weight", "600")
                    property("color", WebTextPrimary); property("background", WebSurfaceDim)
                }
            },
        ) { Text(state.organizationName ?: "…") }
    }
}

/** Takes primitives rather than [WebSettingsState]/[WebSettingsIntent] directly so it's reusable
 * from [AdminSettingsContent] too — language is an account-level preference, not Organisation-scoped,
 * so it applies identically to both Entreprise and Admin accounts. */
@Composable
internal fun LanguageCard(strings: SettingsStrings, selectedLanguage: String, languageError: String?, onSelectLanguage: (String) -> Unit) {
    SettingsCard(title = strings.languageSectionTitle) {
        LanguageDropdown(strings = strings, selected = selectedLanguage, onSelect = onSelectLanguage)
        if (languageError != null) {
            Div(attrs = { style { property("margin-top", "10px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = strings.languageErrorGeneric)
            }
        }
    }
}

/** MIL-17/legal — 2 liens vers [WebLegalScreen] (Conditions d'utilisation / Confidentialité & RGPD).
 * Reusable as-is from [AdminSettingsContent] — the legal documents apply to any account, not just
 * Entreprise. */
@Composable
internal fun LegalCard(strings: SettingsStrings, onNavigateToTerms: () -> Unit, onNavigateToPrivacy: () -> Unit) {
    SettingsCard(title = strings.legalSectionTitle) {
        LegalLinkRow(label = strings.legalTermsLabel, onClick = onNavigateToTerms)
        Div(attrs = { style { property("height", "1px"); property("background", WebSurfaceDim); property("margin", "4px 0") } })
        LegalLinkRow(label = strings.legalPrivacyLabel, onClick = onNavigateToPrivacy)
    }
}

@Composable
private fun LegalLinkRow(label: String, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                property("cursor", "pointer"); padding(10.px, 0.px)
                property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary)
            }
        },
    ) {
        Text(label)
        Text("›")
    }
}

@Composable
private fun LanguageDropdown(strings: SettingsStrings, selected: String, onSelect: (String) -> Unit) {
    var isOpen by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    val selectedOption = SETTINGS_LANGUAGES.firstOrNull { it.code == selected } ?: SETTINGS_LANGUAGES.first()

    Div(attrs = { style { property("position", "relative") } }) {
        Div(
            attrs = {
                onClick { isOpen = !isOpen }
                style {
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    padding(11.px, 14.px); property("cursor", "pointer"); property("background", WebSurface)
                }
            },
        ) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
                LanguageFlag(selectedOption.flagFile)
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                    Text(selectedOption.nativeName)
                }
            }
            Text(if (isOpen) "▲" else "▼")
        }

        if (isOpen) {
            val ordered = SETTINGS_LANGUAGES.sortedByDescending { it.code == selected }
            // All 10 languages shown directly — no longer truncated to 5 with a "+N more" line;
            // the search field still narrows the list down if the user types.
            val visible = if (query.isBlank()) {
                ordered
            } else {
                ordered.filter { it.nativeName.contains(query, ignoreCase = true) || it.code.contains(query, ignoreCase = true) }
            }

            Div(
                attrs = {
                    style {
                        property("position", "absolute")
                        property("top", "calc(100% + 6px)"); property("left", "0"); property("right", "0")
                        property("background", WebSurface); property("border", "1px solid $WebBorder")
                        property("border-radius", "10px"); property("box-shadow", "0 12px 28px rgba(30,58,74,0.18)")
                        property("z-index", "50"); property("overflow", "hidden")
                    }
                },
            ) {
                Div(attrs = { style { padding(10.px, 12.px); property("border-bottom", "1px solid $WebBorder") } }) {
                    Input(
                        type = InputType.Text,
                        attrs = {
                            value(query)
                            attr("placeholder", strings.languageSearchPlaceholder)
                            onInput { event -> query = event.value }
                            style {
                                width(100.percent); property("box-sizing", "border-box")
                                property("border", "1px solid $WebBorder"); property("border-radius", "7px")
                                property("padding", "8px 10px"); property("font-size", "13px")
                            }
                        },
                    )
                }
                Div(attrs = { style { property("max-height", "260px"); property("overflow-y", "auto") } }) {
                    visible.forEach { option ->
                        LanguageRow(option = option, isSelected = option.code == selected) {
                            onSelect(option.code)
                            isOpen = false
                            query = ""
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LanguageRow(option: LanguageOption, isSelected: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                padding(10.px, 14.px); property("cursor", "pointer")
                property("background", if (isSelected) WebActiveTint else WebSurface)
                property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
            LanguageFlag(option.flagFile)
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextPrimary) } }) { Text(option.nativeName) }
        }
        if (isSelected) Text("✓")
    }
}

@Composable
private fun LanguageFlag(flagFile: String) {
    Img(
        src = drawableUri(flagFile),
        alt = "",
        attrs = {
            style {
                width(22.px); height(15.px)
                property("border", "1px solid $WebBorder"); property("border-radius", "2px")
                property("flex-shrink", "0"); property("object-fit", "cover")
            }
        },
    )
}

@Composable
private fun SubscriptionCard(strings: SettingsStrings, subscriptionStrings: com.milta.core.localization.SubscriptionStrings, state: WebSettingsState) {
    SettingsCard(title = strings.subscriptionSectionTitle) {
        val planLabel = when (state.subscriptionPlan) {
            SubscriptionPlan.Organisation -> strings.subscriptionPlanOrganisationLabel
            SubscriptionPlan.Driver -> subscriptionStrings.planDriver
            null -> strings.subscriptionUnavailable
        }
        val price = state.subscriptionPrice
        val currency = state.subscriptionCurrency
        val priceLine = if (price != null && currency != null) {
            "${formatPrice(price, currency)} ${strings.subscriptionPriceSuffix}"
        } else null
        val billingLabel = when (state.subscriptionBillingPeriod) {
            "Monthly" -> strings.subscriptionBillingMonthly
            "Annual" -> strings.subscriptionBillingAnnual
            else -> strings.subscriptionUnavailable
        }
        val renewalLabel = state.subscriptionExpiresAt
            ?.let { formatExpiryDate(it, subscriptionStrings.monthNames) }
            ?: subscriptionStrings.noExpiry

        SubscriptionRow(label = strings.subscriptionPlanLabel, valueLines = listOfNotNull(planLabel, priceLine))
        SubscriptionRow(label = strings.subscriptionBillingLabel, valueLines = listOf(billingLabel))
        SubscriptionRow(label = strings.subscriptionRenewalLabel, valueLines = listOf(renewalLabel), showDivider = false)
    }
}

/**
 * MIL-98 — E2 "Mon usage" (former MIL-97 "Mes optimisations" card): two tabs, "Optimisations" (the
 * MIL-97 content, unchanged) and "Crédit IA". Opens on "Crédit IA", as on the mockup.
 */
@Composable
private fun MyUsageCard() {
    val ai = LocalStrings.current.aiCredit
    var aiTab by remember { mutableStateOf(true) }
    SettingsCard(title = ai.myUsageTitle) {
        Div(attrs = { style { property("display", "flex"); property("gap", "6px"); property("margin", "-4px 0 14px"); property("border-bottom", "1px solid $WebBorder") } }) {
            UsageTab(ai.tabOptimizations, selected = !aiTab) { aiTab = false }
            UsageTab(ai.tabAiCredit, selected = aiTab) { aiTab = true }
        }
        if (aiTab) MyAiCreditTabContent() else MyOptimizationsTabContent()
    }
}

@Composable
private fun UsageTab(label: String, selected: Boolean, onSelect: () -> Unit) {
    Div(attrs = {
        onClick { onSelect() }
        style {
            padding(9.px, 14.px); property("font-size", "13.5px"); property("cursor", "pointer"); property("margin-bottom", "-1px")
            property("font-weight", if (selected) "700" else "500")
            property("color", if (selected) WebPrimary else WebTextSecondary)
            property("border-bottom", if (selected) "2px solid $WebTealAccent" else "2px solid transparent")
        }
    }) { Text(label) }
}

/** MIL-98 — tab "Crédit IA": percentage only, reset date, paged history with the share of each call (R1, R13). */
@Composable
private fun MyAiCreditTabContent() {
    val creditStore: AiCreditStore = koinInject()
    val getMyHistory: GetMyAiCreditHistoryUseCase = koinInject()
    val viewModel = remember { MyAiCreditViewModel(creditStore, getMyHistory) }
    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }
    val state by viewModel.state.collectAsState()
    val ai = LocalStrings.current.aiCredit
    val quotaStrings = LocalStrings.current.optimizationQuota

    val credit = state.credit
    when {
        credit != null -> {
            Div(attrs = { style { property("display", "flex"); property("align-items", "baseline"); property("gap", "8px") } }) {
                Div(attrs = {
                    style {
                        property("font-size", "26px"); property("font-weight", "800")
                        property("color", when { credit.isExhausted -> WebErrorInk; credit.isLow -> WebLowInk; else -> WebPrimary })
                    }
                }) { Text(ai.percent(credit.remainingPercent)) }
                Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(ai.remainingSuffix) }
            }
            CreditMeter(credit.remainingPercent, low = credit.isLow)
            SubscriptionRow(
                label = quotaStrings.resetLabel,
                valueLines = listOf(OptimizationQuotaFormat.fullDate(credit.resetAt), quotaStrings.resetAtRenewal),
            )
        }
        state.isLoadingCredit -> WebSpinner(size = 20, strokeWidth = 2)
        else -> Div(attrs = {
            style {
                property("font-size", "13px"); property("color", WebErrorInk); property("margin-bottom", "8px")
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
            }
        }) {
            Text(if (state.creditError == "offline") quotaStrings.errorNoConnection else quotaStrings.errorGeneric)
            Button(attrs = { onClick { viewModel.onIntent(MyAiCreditIntent.Load) }; style { secondaryButtonStyle(this) } }) {
                Text(quotaStrings.retryAction)
            }
        }
    }
    Div(attrs = { style { property("margin-top", "12px") } }) {
        AiCreditHistoryTable(
            history = state.history,
            columns = listOf(
                AiHistoryColumn(quotaStrings.columnDate),
                AiHistoryColumn(quotaStrings.columnAction),
                AiHistoryColumn(quotaStrings.columnOutcome),
                AiHistoryColumn(ai.columnShare, alignRight = true),
            ),
            footerStrings = quotaStrings,
            onLoadMore = { viewModel.onIntent(MyAiCreditIntent.LoadMoreHistory) },
        ) { entry ->
            AiHistoryCell(OptimizationQuotaFormat.dayMonthTime(entry.occurredAt))
            AiHistoryCell(ai.actionLabel(entry.action))
            AiHistoryCellContent { AiOutcomePill(entry.outcome, ai) }
            AiHistoryCell(ai.share(entry.creditPercent), alignRight = true)
        }
    }
    Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "10px") } }) {
        Text(ai.webScopeHint)
    }
}

/** MIL-97 — E3 "Mes optimisations": only this account's counter, never a total of its drivers (R1).
 * MIL-98 — the "Optimisations" tab of [MyUsageCard], content unchanged. */
@Composable
private fun MyOptimizationsTabContent() {
    val quotaStore: OptimizationQuotaStore = koinInject()
    val getMyHistory: GetMyOptimizationHistoryUseCase = koinInject()
    val viewModel = remember { MyOptimizationsViewModel(quotaStore, getMyHistory) }
    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }
    val state by viewModel.state.collectAsState()
    val quotaStrings = LocalStrings.current.optimizationQuota

    run {
        val quota = state.quota
        when {
            quota != null -> {
                Div(attrs = { style { property("display", "flex"); property("align-items", "baseline"); property("gap", "8px"); property("margin-bottom", "4px") } }) {
                    Div(attrs = {
                        style {
                            property("font-size", "26px"); property("font-weight", "800")
                            property("color", if (quota.isExhausted) WebErrorInk else WebPrimary)
                        }
                    }) { Text(quota.ratioLabel) }
                    Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(quotaStrings.remainingSuffix) }
                }
                SubscriptionRow(
                    label = quotaStrings.resetLabel,
                    valueLines = listOf(OptimizationQuotaFormat.fullDate(quota.resetAt), quotaStrings.resetAtRenewal),
                )
            }
            state.isLoadingQuota -> WebSpinner(size = 20, strokeWidth = 2)
            else -> Div(attrs = { style { property("font-size", "13px"); property("color", WebErrorInk); property("margin-bottom", "8px") } }) {
                Text(if (state.quotaError == "offline") quotaStrings.errorNoConnection else quotaStrings.errorGeneric)
            }
        }
        Div(attrs = { style { property("margin-top", "12px") } }) {
            OptimizationHistoryTable(
                history = state.history,
                strings = quotaStrings,
                onLoadMore = { viewModel.onIntent(MyOptimizationsIntent.LoadMoreHistory) },
            )
        }
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "10px") } }) {
            Text(quotaStrings.webCounterNotice)
        }
    }
}

@Composable
private fun SubscriptionRow(label: String, valueLines: List<String>, showDivider: Boolean = true) {
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "flex-start"); property("justify-content", "space-between")
                padding(12.px, 0.px)
                if (showDivider) property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) {
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(label) }
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("align-items", "flex-end"); property("gap", "2px") } }) {
            valueLines.forEach { line ->
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary); property("text-align", "right") } }) {
                    Text(line)
                }
            }
        }
    }
}

@Composable
private fun DeleteZoneCard(
    strings: SettingsStrings,
    commonStrings: com.milta.core.localization.CommonStrings,
    state: WebSettingsState,
    onIntent: (WebSettingsIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-left", "4px solid $WebError")
                property("border-radius", "12px")
                padding(22.px)
            }
        },
    ) {
        Div(attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } }) {
            Text(strings.deleteSectionTitle)
        }
        Div(attrs = { style { property("font-size", "13px"); property("color", WebErrorInk); property("font-weight", "600"); property("margin-bottom", "10px") } }) {
            Text(strings.deleteWarning)
        }
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("line-height", "1.5"); property("margin-bottom", "18px") } }) {
            Text(strings.deleteDescription)
        }

        Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary); property("margin-bottom", "8px") } }) {
            Text(strings.deleteConfirmLabel)
        }
        Input(
            type = InputType.Text,
            attrs = {
                value(state.deleteConfirmationInput)
                attr("placeholder", state.organizationName.orEmpty())
                onInput { event -> onIntent(WebSettingsIntent.UpdateDeleteConfirmationInput(event.value)) }
                style {
                    width(100.percent); property("box-sizing", "border-box")
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    property("padding", "11px 14px"); property("font-size", "13.5px"); property("margin-bottom", "16px")
                }
            },
        )

        val nameMatches = state.organizationName != null && state.deleteConfirmationInput == state.organizationName
        Button(
            attrs = {
                if (nameMatches) onClick { onIntent(WebSettingsIntent.OpenDeleteDialog) }
                style {
                    property("border", "1px solid $WebError"); property("border-radius", "8px")
                    property("background", if (nameMatches) WebSurface else WebSurfaceDim)
                    property("color", WebError)
                    property("font-size", "13px"); property("font-weight", "700")
                    property("padding", "10px 16px"); property("cursor", if (nameMatches) "pointer" else "not-allowed")
                    property("opacity", if (nameMatches) "1" else "0.5")
                }
            },
        ) { Text(strings.deleteButton) }
    }
}

@Composable
private fun DeleteConfirmDialog(
    strings: SettingsStrings,
    commonStrings: com.milta.core.localization.CommonStrings,
    state: WebSettingsState,
    onIntent: (WebSettingsIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", WebSurface); property("border-radius", "14px")
                    width(380.px); property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                }
            },
        ) {
            Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
                Text(strings.deleteFinalDialogTitle)
            }
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextSecondary); property("line-height", "1.5") } }) {
                Text(strings.deleteFinalDialogMessage)
            }

            val error = state.deleteError
            if (error != null) {
                Div(attrs = { style { property("margin-top", "12px") } }) {
                    InlineBanner(kind = InlineBannerKind.Error, text = strings.deleteErrorGeneric)
                }
            }

            Div(attrs = { style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "10px"); property("margin-top", "20px") } }) {
                Button(
                    attrs = {
                        onClick { onIntent(WebSettingsIntent.DismissDeleteDialog) }
                        style {
                            property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("background", WebSurface); property("color", WebTextPrimary)
                            property("font-size", "13px"); property("font-weight", "600")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(commonStrings.cancel) }

                Button(
                    attrs = {
                        if (!state.isDeleting) onClick { onIntent(WebSettingsIntent.ConfirmDelete) }
                        style {
                            property("border", "none"); property("border-radius", "8px")
                            property("background", WebError); property("color", "#FFFFFF")
                            property("font-size", "13px"); property("font-weight", "700")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                            if (state.isDeleting) { property("opacity", "0.6"); property("cursor", "not-allowed") }
                        }
                    },
                ) { Text(strings.deleteButton) }
            }
        }
    }
}

private fun formatPrice(price: Double, currency: String): String {
    val symbol = when (currency) {
        "EUR" -> "€"
        "USD" -> "$"
        else -> currency
    }
    val formatted = price.toString().let { if (it.endsWith(".0")) it.dropLast(2) else it }
    return "$formatted $symbol"
}

/** Formats an ISO-8601 timestamp as "10 juillet 2026" — same helper as
 * [com.milta.feature.subscription.presentation.SubscriptionScreen]'s private `formatExpiryDate`. */
private fun formatExpiryDate(iso: String, monthNames: List<String>): String {
    val datePart = iso.substringBefore('T')
    val parts = datePart.split("-")
    if (parts.size != 3) return iso
    val (year, month, day) = parts
    val monthName = month.toIntOrNull()?.let { monthNames.getOrNull(it - 1) } ?: month
    return "${day.toIntOrNull() ?: day} $monthName $year"
}
